import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import type {
  DeviceInfo,
  MeasurementPoint,
  PointBinding,
  PointReading,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t, tOr } from "../i18n.ts";
import {
  formatAgo,
  formatDateTime,
  formatNumber,
  formatShare,
  formatTime,
} from "../format.ts";
import {
  pathTo,
  periodRange,
  range,
  reloadTree,
  siteTree,
} from "../context.ts";
import { coverage, dataState } from "../assets.ts";
import { summed } from "../pointData.ts";
import { batteryLow, LATE_AFTER_MS } from "../overview.ts";
import { familyOf } from "../supply.ts";
import { SupplyPanel } from "./SupplyPanel.tsx";
import { RuleWizard } from "./RuleWizard.tsx";
import { newRule } from "../events.ts";
import { Icon } from "../icons.tsx";
import { Chart, type ChartSeries } from "./Chart.tsx";
import { MediumBadge } from "./Media.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import {
  ConfirmDialog,
  Dialog,
  operator,
  Skeleton,
  Tabs,
  useAction,
} from "./ui.tsx";

export const READINESS_CHIP: Record<string, string> = {
  project: "dash",
  config: "warn",
  check: "acc",
  running: "ok",
  paused: "stale",
  retired: "stale",
};
const DATA_CHIP: Record<string, string> = {
  ok: "ok",
  late: "warn",
  none: "crit",
};
const STEP_MS = 15 * 60_000;
/** Binding start meaning "all the channel's data" (the host's PointBinding.SinceFirstReading). */
const SINCE_FIRST_READING = new Date(0).toISOString();
const ENERGY_MEDIA = new Set(["el", "pv", "bess"]);
const TABS = ["summary", "data", "device", "supply"] as const;
type Tab = (typeof TABS)[number];

/** A unit as people read it (degC → °C), from the shared unit labels. */
export const unitLabel = (unit: string | null | undefined) =>
  unit ? tOr(`unit.${unit}`, unit) : "";

/** A point's main value now with its unit, or a dash. */
export function ReadingValue(props: { reading: PointReading | undefined }) {
  return (
    <Show
      when={props.reading?.value !== null && props.reading?.value !== undefined}
      fallback="—"
    >
      {formatNumber(
        props.reading!.value,
        locale(),
        Math.abs(props.reading!.value!) < 10 ? 2 : 1,
      )}{" "}
      {unitLabel(props.reading!.unit)}
    </Show>
  );
}

/**
 * Data state of a point as a chip: current, late since, no data; a dash for a point without a device. A condition
 * sensor that reports on change shows when its last value came instead of "current", without a warning, until its
 * silence is long enough to be a fault. A device battery shows its level, and a warning when it needs replacing.
 */
export function DataChip(props: {
  point: MeasurementPoint;
  reading: PointReading | undefined;
}) {
  const state = () => dataState(props.point, props.reading, Date.now());
  const quiet = () =>
    state() === "ok" &&
    props.point.role === "environment" &&
    !!props.reading?.at &&
    Date.now() - new Date(props.reading.at).getTime() > LATE_AFTER_MS;
  const low = () => !!props.reading && batteryLow(props.reading);
  return (
    <Show when={state() !== "na"} fallback="—">
      <span style={{ display: "inline-flex", gap: "4px", "flex-wrap": "wrap" }}>
        <span class={`chip ${quiet() ? "" : (DATA_CHIP[state()] ?? "")}`}>
          {quiet()
            ? t("overview.lastReading", {
                ago: formatAgo(props.reading?.at, locale()),
              })
            : state() === "ok"
              ? t("overview.current")
              : state() === "late"
                ? t("overview.lateSince", {
                    ago: formatAgo(props.reading?.at, locale()),
                  })
                : t("overview.noReading")}
        </span>
        <Show
          when={
            props.reading?.batteryLevel != null ||
            props.reading?.batteryState === "low" ||
            props.reading?.batteryState === "empty"
          }
        >
          <span
            class={`chip ${low() ? "crit" : ""}`}
            title={t("overview.battery.title")}
          >
            <Icon name="battery" />{" "}
            {props.reading?.batteryState === "empty"
              ? t("overview.battery.empty")
              : low()
                ? t("overview.battery.low", {
                    level:
                      props.reading?.batteryLevel != null
                        ? `${formatNumber(props.reading.batteryLevel, locale(), 0)} %`
                        : "",
                  })
                : `${formatNumber(props.reading!.batteryLevel!, locale(), 0)} %`}
          </span>
        </Show>
      </span>
    </Show>
  );
}

const sinceText = (b: PointBinding) =>
  new Date(b.validFrom).getTime() === 0
    ? t("assets.sinceFirstReading")
    : t("assets.since", { at: formatDateTime(b.validFrom, locale()) });

/**
 * One measurement point (prototype "punkt"): readiness, data state and value now, its main quantity over the period
 * with gaps, figures for the period, data completeness, and which device measured it when.
 */
export function PointCard(props: {
  point: MeasurementPoint;
  reading: PointReading | undefined;
  onEdit: () => void;
  onChanged: () => Promise<void>;
  onDeleted: () => void;
  onSelectNode: (id: string) => void;
}) {
  const [tab, setTab] = createSignal<Tab>("summary");
  const [devices] = createResource(() => api.devices.list());
  const [history, { refetch }] = createResource(
    () => props.point.id,
    (id) => api.sites.pointBindings(id),
  );
  const [binding, setBinding] = createSignal(false);
  const [ruling, setRuling] = createSignal(false);
  // Annotations of the point in the period (added in Analyses), on its chart.
  const [notes] = createResource(
    () => ({ id: props.point.id, ...range() }),
    (q) => api.annotations.list([q.id], q.from, q.to),
  );
  const [remove, setRemove] = createSignal(false);
  const [run, error, busy] = useAction();
  const deviceName = (id: string) =>
    devices()?.find((d) => d.id === id)?.name ?? id;
  const crumbs = () => pathTo(siteTree()?.nodes ?? [], props.point.nodeId);
  const b = () => props.point.binding;
  const energy = () => ENERGY_MEDIA.has(props.point.medium);
  const attribute = () =>
    props.reading?.attribute ?? (energy() ? "power.active" : null);
  const unit = () => unitLabel(props.reading?.unit ?? (energy() ? "kW" : ""));

  // The current binding's data over the period (older devices are listed under "device").
  const window = (from: Date, to: Date) => {
    const start = Math.max(from.getTime(), new Date(b()!.validFrom).getTime());
    const end = Math.min(to.getTime(), Date.now());
    return { from: new Date(start), to: new Date(Math.max(start, end)) };
  };
  // Channels the point's value comes from: the phases of a meter that reports power only per phase.
  const channels = () =>
    props.reading && props.reading.channels.length > 0
      ? props.reading.channels
      : b()
        ? [b()!.channel]
        : [];
  const [series] = createResource(
    () =>
      b() && attribute()
        ? {
            b: b()!,
            attribute: attribute()!,
            channels: channels().join(),
            ...range(),
          }
        : undefined,
    (q) => {
      const w = window(q.from, q.to);
      return summed(
        q.b.deviceId,
        q.channels.split(","),
        q.attribute,
        w.from,
        w.to,
      );
    },
  );
  const [counter] = createResource(
    () => (b() && energy() ? { b: b()!, ...range() } : undefined),
    (q) => {
      // As the balance counts it: all bindings of the point, balanced registers, an inverted meter's export.
      const w = window(q.from, q.to);
      return api.sites.pointEnergy(
        props.point.id,
        w.from,
        w.to,
        w.to.getTime() - w.from.getTime() <= 7 * 86_400_000 ? "15m" : "hour",
      );
    },
  );
  const [today] = createResource(
    () =>
      b() && attribute()
        ? { b: b()!, attribute: attribute()!, channels: channels().join() }
        : undefined,
    (q) => {
      const r = periodRange("today");
      const w = window(r.from, r.to);
      return summed(
        q.b.deviceId,
        q.channels.split(","),
        q.attribute,
        w.from,
        w.to,
      );
    },
  );

  const points = createMemo(() =>
    (series() ?? []).map((p) => ({ t: new Date(p.at).getTime(), v: p.value })),
  );
  const values = createMemo(
    () => points().filter((p) => p.v !== null) as { t: number; v: number }[],
  );
  const periodCoverage = createMemo(() => {
    if (!b()) {
      return null;
    }
    const w = window(range().from, range().to);
    return coverage(
      values().map((p) => p.t),
      w.from.getTime(),
      w.to.getTime(),
      STEP_MS,
    );
  });
  const todayCoverage = createMemo(() => {
    if (!b() || !today()) {
      return null;
    }
    const r = periodRange("today");
    const w = window(r.from, r.to);
    return coverage(
      today()!
        .filter((p) => p.value !== null)
        .map((p) => new Date(p.at).getTime()),
      w.from.getTime(),
      w.to.getTime(),
      STEP_MS,
    );
  });
  const stats = createMemo(() => {
    const v = values();
    if (v.length === 0) {
      return null;
    }
    const max = v.reduce((a, x) => (x.v > a.v ? x : a));
    const min = v.reduce((a, x) => (x.v < a.v ? x : a));
    return { avg: v.reduce((s, x) => s + x.v, 0) / v.length, max, min };
  });
  // Energy within the period, split into night (22–6) and day; buckets are labelled with their start.
  const energyStats = createMemo(() => {
    const inc = (counter() ?? []).map((p) => ({
      t: new Date(p.at).getTime(),
      v: p.kwh,
    }));
    if (inc.length === 0) {
      return null;
    }
    const total = inc.reduce((s, x) => s + x.v, 0);
    const night = inc.filter((x) => {
      const h = new Date(x.t).getHours();
      return h >= 22 || h < 6;
    });
    return {
      total,
      night: total > 0 ? night.reduce((s, x) => s + x.v, 0) / total : null,
    };
  });
  const chart = createMemo<ChartSeries[]>(() => [
    {
      key: "v",
      label: t(`attribute.${attribute()}`, {}),
      color: `var(--m-${props.point.medium === "temp" ? "temp" : props.point.medium})`,
      kind: energy() ? "area" : "line",
      points: points(),
    },
  ]);

  const unbind = () =>
    void run(async () => {
      await api.sites.unbindPoint(props.point.id);
      await props.onChanged();
      await refetch();
    });
  const sinceFirst = () =>
    void run(async () => {
      await api.sites.measureSinceFirstReading(props.point.id);
      await props.onChanged();
      await refetch();
    });
  const canExtend = () =>
    (history()?.length ?? 0) === 1 &&
    b() !== null &&
    b()!.validFrom !== SINCE_FIRST_READING &&
    new Date(b()!.validFrom).getTime() !== 0;

  return (
    <section class="card" id="detail">
      <div class="card-h">
        <MediumBadge medium={props.point.medium} />
        <div style={{ "min-width": 0 }}>
          <div class="eyebrow">
            {t("assets.point")} ·{" "}
            <For each={crumbs()}>
              {(n, i) => (
                <>
                  {i() > 0 ? " / " : ""}
                  <button
                    type="button"
                    class="linkish"
                    onClick={() => props.onSelectNode(n.id)}
                  >
                    {n.name}
                  </button>
                </>
              )}
            </For>
          </div>
          <h2 style={{ "font-size": "16px" }}>{props.point.name}</h2>
        </div>
        <Show when={operator()}>
          <div
            style={{
              "margin-left": "auto",
              display: "flex",
              gap: "6px",
              "flex-wrap": "wrap",
            }}
          >
            <button
              type="button"
              class={`btn ${b() ? "" : "primary"}`}
              onClick={() => setBinding(true)}
            >
              <Icon name={b() ? "swap" : "link"} />
              {b() ? t("assets.swapDevice") : t("assets.bindDevice")}
            </button>
            <button type="button" class="btn" onClick={() => props.onEdit()}>
              <Icon name="edit" />
              {t("common.edit")}
            </button>
          </div>
        </Show>
      </div>
      <Tabs
        tabs={familyOf(props.point) ? TABS : TABS.filter((x) => x !== "supply")}
        value={tab()}
        onChange={setTab}
        label={(x) => t(`assets.pointTab.${x}`)}
      />
      <div class="card-b stack">
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
        <div class="axes">
          <div class="axis">
            <span class="eyebrow">{t("assets.readiness")}</span>
            <span class={`chip ${READINESS_CHIP[props.point.readiness] ?? ""}`}>
              {t(`readiness.${props.point.readiness}`)}
            </span>
            <span class="q">
              {t(`assets.readinessHint.${props.point.readiness}`)}
            </span>
          </div>
          <div class="axis">
            <span class="eyebrow">{t("assets.freshness")}</span>
            <DataChip point={props.point} reading={props.reading} />
            <span class="q">
              {props.reading?.at
                ? t("assets.lastReading", {
                    at: formatDateTime(props.reading.at, locale()),
                  })
                : b()
                  ? t("overview.noReading")
                  : t("assets.unbound")}
            </span>
          </div>
          <div class="axis">
            <span class="eyebrow">{t("assets.now")}</span>
            <span class="num" style={{ "font-size": "18px" }}>
              <ReadingValue reading={props.reading} />
            </span>
            <span class="q">
              {props.reading?.value === 0
                ? t("overview.measuredZero")
                : props.reading?.attribute
                  ? t(`attribute.${props.reading.attribute}`, {})
                  : ""}
            </span>
          </div>
          <div class="axis">
            <span class="eyebrow">{t("assets.completenessToday")}</span>
            <span class="num" style={{ "font-size": "18px" }}>
              {todayCoverage()?.share === null || todayCoverage() === null
                ? "—"
                : formatShare(todayCoverage()!.share, locale())}
            </span>
            <span class="q">{t("assets.completenessHint")}</span>
          </div>
        </div>

        <Show when={tab() === "summary"}>
          <Show
            when={b()}
            fallback={
              <p class="hint" style={{ margin: 0 }}>
                {t("assets.noDeviceHint")}
              </p>
            }
          >
            <Show
              when={!series.error}
              fallback={<ErrorBox error={series.error} />}
            >
              <Show when={series()} fallback={<Skeleton lines={4} />}>
                <Chart
                  series={chart()}
                  from={range().from}
                  to={range().to}
                  unit={unit()}
                  locale={locale()}
                  bucketMs={STEP_MS}
                  emptyText={t("overview.noData")}
                  gaps={periodCoverage()?.gaps}
                  annotations={(notes() ?? []).map((a) => ({
                    at: new Date(a.at).getTime(),
                    text: a.text,
                    who: a.authorName,
                  }))}
                  gapLabel={t("overview.gap")}
                  now={Date.now()}
                  nowLabel={t("overview.nowMark")}
                  height={200}
                />
              </Show>
            </Show>
            <div class="axes">
              <Show when={energy() && energyStats()}>
                {(e) => (
                  <div class="axis">
                    <span class="eyebrow">{t("assets.energyInPeriod")}</span>
                    <span class="num" style={{ "font-size": "18px" }}>
                      {formatNumber(
                        e().total,
                        locale(),
                        e().total < 10 ? 2 : 1,
                      )}{" "}
                      kWh
                    </span>
                    <span class="q">
                      {e().night === null
                        ? ""
                        : t("assets.nightShare", {
                            share: formatShare(e().night, locale()),
                          })}
                    </span>
                  </div>
                )}
              </Show>
              <Show when={stats()}>
                {(s) => (
                  <>
                    <div class="axis">
                      <span class="eyebrow">{t("assets.average")}</span>
                      <span class="num" style={{ "font-size": "18px" }}>
                        {formatNumber(s().avg, locale(), 2)} {unit()}
                      </span>
                      <span class="q">{t("assets.of15m")}</span>
                    </div>
                    <div class="axis">
                      <span class="eyebrow">{t("assets.maximum")}</span>
                      <span class="num" style={{ "font-size": "18px" }}>
                        {formatNumber(s().max.v, locale(), 2)} {unit()}
                      </span>
                      <span class="q">
                        {formatDateTime(new Date(s().max.t), locale())}
                      </span>
                    </div>
                    <Show when={!energy()}>
                      <div class="axis">
                        <span class="eyebrow">{t("assets.minimum")}</span>
                        <span class="num" style={{ "font-size": "18px" }}>
                          {formatNumber(s().min.v, locale(), 2)} {unit()}
                        </span>
                        <span class="q">
                          {formatDateTime(new Date(s().min.t), locale())}
                        </span>
                      </div>
                    </Show>
                  </>
                )}
              </Show>
            </div>
          </Show>
          <dl class="kv">
            <dt>{t("assets.role")}</dt>
            <dd>
              {t(`role.point.${props.point.role}`)}
              <div class="hint">{t(`role.pointHint.${props.point.role}`)}</div>
            </dd>
            <dt>{t("assets.device")}</dt>
            <dd>
              <Show
                when={b()}
                fallback={<span class="chip warn">{t("assets.unbound")}</span>}
              >
                {(x) => (
                  <>
                    <a href={`/devices/${x().deviceId}`}>
                      {deviceName(x().deviceId)}
                    </a>{" "}
                    · {tOr(`channel.${x().channel}`, x().channel)}
                    <div class="hint">{sinceText(x())}</div>
                  </>
                )}
              </Show>
            </dd>
            <dt>{t("assets.boundary")}</dt>
            <dd>
              {props.point.role === "main"
                ? t("assets.isBoundary")
                : t("assets.notBoundary")}
            </dd>
          </dl>
        </Show>

        <Show when={tab() === "data" && operator() && b()}>
          <div>
            <button
              type="button"
              class="btn sm"
              onClick={() => setRuling(true)}
            >
              <Icon name="bell" />
              {t("events.ruleForPoint")}
            </button>
          </div>
        </Show>
        <Show when={ruling()}>
          <RuleWizard
            initial={newRule(
              props.point.id,
              t("events.wizard.defaultName", { name: props.point.name }),
            )}
            onClose={() => setRuling(false)}
            onSaved={() => api.invalidate("/api/v1/alert-rules")}
          />
        </Show>
        <Show when={tab() === "data"}>
          <Show
            when={b()}
            fallback={
              <p class="hint" style={{ margin: 0 }}>
                {t("assets.noDeviceHint")}
              </p>
            }
          >
            <dl class="kv">
              <dt>{t("assets.completenessPeriod")}</dt>
              <dd>
                {periodCoverage()?.share === null || !periodCoverage()
                  ? "—"
                  : formatShare(periodCoverage()!.share, locale())}
              </dd>
              <dt>{t("assets.readings")}</dt>
              <dd>{t("assets.readingsCount", { count: values().length })}</dd>
              <dt>{t("assets.quantity")}</dt>
              <dd>
                {attribute() ? t(`attribute.${attribute()}`, {}) : "—"} ·{" "}
                {unit()}
              </dd>
            </dl>
            <div>
              <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
                {t("assets.gaps")}
              </div>
              <Show
                when={(periodCoverage()?.gaps.length ?? 0) > 0}
                fallback={
                  <p class="hint" style={{ margin: 0 }}>
                    {t("assets.noGaps")}
                  </p>
                }
              >
                <table class="tbl">
                  <tbody>
                    <For
                      each={[...(periodCoverage()?.gaps ?? [])]
                        .reverse()
                        .slice(0, 12)}
                    >
                      {(g) => (
                        <tr>
                          <td class="num">
                            {formatDateTime(new Date(g.from), locale())} –{" "}
                            {formatTime(new Date(g.to), locale())}
                          </td>
                          <td class="r num">
                            {formatNumber(
                              (g.to - g.from) / 3_600_000,
                              locale(),
                              2,
                            )}{" "}
                            h
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </Show>
              <p class="hint">{t("assets.gapsHint")}</p>
            </div>
          </Show>
        </Show>

        <Show when={tab() === "supply" && familyOf(props.point)}>
          <SupplyPanel point={props.point} onChanged={props.onChanged} />
        </Show>

        <Show when={tab() === "device"}>
          <Show when={operator()}>
            <div class="row-acts">
              <Show when={b()}>
                <button
                  type="button"
                  class="btn sm"
                  disabled={busy()}
                  onClick={unbind}
                >
                  {t("assets.unbindDevice")}
                </button>
              </Show>
              <Show when={canExtend()}>
                <button
                  type="button"
                  class="btn sm"
                  disabled={busy()}
                  onClick={sinceFirst}
                >
                  <Icon name="clock" />
                  {t("assets.useEarlierData")}
                </button>
              </Show>
              <button
                type="button"
                class="btn sm danger"
                style={{ "margin-left": "auto" }}
                onClick={() => setRemove(true)}
              >
                {t("common.delete")}
              </button>
            </div>
            <Show when={canExtend()}>
              <p class="hint" style={{ margin: 0 }}>
                {t("assets.useEarlierDataHint")}
              </p>
            </Show>
          </Show>
          <div>
            <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
              {t("assets.history")}
            </div>
            <Show
              when={(history()?.length ?? 0) > 0}
              fallback={
                <p class="hint" style={{ margin: 0 }}>
                  {t("assets.noHistory")}
                </p>
              }
            >
              <ul class="timeline">
                <For each={history()}>
                  {(h) => (
                    <li>
                      <time>
                        {new Date(h.validFrom).getTime() === 0
                          ? t("assets.firstReading")
                          : formatDateTime(h.validFrom, locale())}
                      </time>
                      <span class={`mk ${h.validTo === null ? "ok" : ""}`} />
                      <div>
                        <b style={{ "font-weight": 500 }}>
                          {deviceName(h.deviceId)} ·{" "}
                          {tOr(`channel.${h.channel}`, h.channel)}
                        </b>
                        <div class="hint">
                          {h.validTo === null
                            ? t("assets.current")
                            : t("assets.until", {
                                at: formatDateTime(h.validTo, locale()),
                              })}
                          {h.invert ? ` · ${t("assets.inverted")}` : ""}
                        </div>
                      </div>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </div>
        </Show>
      </div>
      <Show when={binding()}>
        <BindDialog
          point={props.point}
          devices={devices() ?? []}
          onClose={() => setBinding(false)}
          onSaved={async () => {
            await props.onChanged();
            await refetch();
          }}
        />
      </Show>
      <Show when={remove()}>
        <ConfirmDialog
          title={t("assets.deletePointTitle", { name: props.point.name })}
          text={t("assets.deletePointText")}
          confirm={t("common.delete")}
          danger
          onConfirm={async () => {
            await api.sites.removePoint(props.point.id);
            await reloadTree();
            props.onDeleted();
          }}
          onClose={() => setRemove(false)}
        />
      </Show>
    </section>
  );
}

/** Which device channel measures the point from now (or a given moment) on; the previous binding is closed then. */
function BindDialog(props: {
  point: MeasurementPoint;
  devices: DeviceInfo[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [deviceId, setDeviceId] = createSignal(
    props.point.binding?.deviceId ?? "",
  );
  const [channel, setChannel] = createSignal(
    props.point.binding?.channel ?? "",
  );
  const [invert, setInvert] = createSignal(false);
  const [from, setFrom] = createSignal("");
  const [run, error, busy] = useAction();
  const [detail] = createResource(deviceId, (id) =>
    id ? api.devices.get(id) : Promise.resolve(undefined),
  );
  const channels = () => detail()?.template.channels ?? [];

  const save = (e: SubmitEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.sites.bindPoint(
        props.point.id,
        deviceId(),
        channel() || channels()[0]!.key,
        invert(),
        from() ? new Date(from()) : null,
      );
      await props.onSaved();
      props.onClose();
    });
  };

  return (
    <Dialog
      title={t("assets.bindTitle", { name: props.point.name })}
      onClose={props.onClose}
    >
      <form class="stack" onSubmit={save}>
        <div class="field">
          <label for="bindDevice">{t("assets.device")}</label>
          <select
            id="bindDevice"
            required
            value={deviceId()}
            onChange={(e) => {
              setDeviceId(e.currentTarget.value);
              setChannel("");
            }}
          >
            <option value="" disabled>
              {t("assets.chooseDevice")}
            </option>
            <For
              each={[...props.devices].sort((a, b) =>
                a.name.localeCompare(b.name),
              )}
            >
              {(d) => (
                <option value={d.id}>
                  {d.name} · {t(`category.${d.category}`)}
                </option>
              )}
            </For>
          </select>
        </div>
        <Show when={channels().length > 1}>
          <div class="field">
            <label for="bindChannel">{t("assets.channel")}</label>
            <select
              id="bindChannel"
              value={channel() || channels()[0]!.key}
              onChange={(e) => setChannel(e.currentTarget.value)}
            >
              <For each={channels()}>
                {(c) => (
                  <option value={c.key}>
                    {tOr(`channel.${c.key}`, c.key)}
                  </option>
                )}
              </For>
            </select>
          </div>
        </Show>
        <label class="opt">
          <input
            type="checkbox"
            checked={invert()}
            onChange={(e) => setInvert(e.currentTarget.checked)}
          />
          <span>
            <b>{t("assets.invert")}</b>
            <small>{t("assets.invertHint")}</small>
          </span>
        </label>
        <div class="field">
          <label for="bindFrom">{t("assets.from")}</label>
          <input
            id="bindFrom"
            type="datetime-local"
            value={from()}
            onInput={(e) => setFrom(e.currentTarget.value)}
          />
          <p class="hint">{t("assets.fromHint")}</p>
        </div>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
        <div class="row-acts" style={{ "justify-content": "flex-end" }}>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            class="btn primary"
            disabled={busy() || !deviceId() || channels().length === 0}
          >
            {t("assets.bind")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
